import { useCallback, useState } from 'react';

export type UseControllableStateParams<T> = {
  prop: T | undefined;
  defaultProp: T;
  onChange?: (next: T) => void;
};

/**
 * Controlled/uncontrolled state pattern: derives from `prop` when provided, otherwise manages its own state.
 * onChange always fires from the setter, so callers get one API regardless of who owns state.
 */
export function useControllableState<T>({ prop, defaultProp, onChange }: UseControllableStateParams<T>): [T, (next: T) => void] {
  const isControlled = prop !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultProp);
  const value = isControlled ? prop : uncontrolledValue;

  const setValue = useCallback(
    (next: T) => {
      if (!isControlled) {
        setUncontrolledValue(next);
      }
      onChange?.(next);
    },
    [isControlled, onChange],
  );

  return [value, setValue];
}
